import { Array as A, Option as O } from '@effect-ts/core'
import { pipe } from '@effect-ts/core/Function'
import _assign from 'lodash/assign'
import _find from 'lodash/find'
import _get from 'lodash/get'
import _map from 'lodash/map'
import type { Module } from 'vuex'

import axios from '../../_framework/axios/trip-dashboard-axios'
import type { Role } from '../../_stores/modules/Users/models/Role'
import type { User } from '../../_stores/modules/Users/models/User'
import { TripDashboardViewModes } from '../TripDashboardPermissionBusConstants'
import { alertAxiosError } from '../utils/alertAxiosError'
import { TripDashboardStore, TripDashboardStoreRootState } from './_store_trip_dashboard'
// @ts-ignore
import { setupDashboard } from './TripDashboardsCollection'

// TODO: type
const defaultMockUser = {
  email: 'jane.smith@email.com',
  name: 'Jane Smith',
  id: -1,
  roles: [
    'Client'
  ]
}
// TODO: types...
const userHasRole = (user: User, role: string) =>
  pipe(
    user.roles,
    A.find((r: Role) => r.toString() === role),
    O.isSome
  )

export const UsersStoreModule: Module<
  {
    // TODO:
    currentUser: any
    // TODO:
    clients: any[]
    // TODO:
    contacts: any[]
    impersonatingUserId: O.Option<number> // <-1 is default mock user
    currentViewMode: O.Option<number> // TODO: clean this type
  },
  TripDashboardStoreRootState
> = {
  state: {
    currentUser: _get(window, 'appInitialData.stores.User', null),
    contacts: _get(window, 'appInitialData.stores.Contacts', []),
    impersonatingUserId: O.none,
    clients: [],
    currentViewMode: O.none
  },
  getters: {
    currentRealUser: state => state.currentUser,
    currentRealUserIsOperator: state =>
      state.currentUser
      && (userHasRole(state.currentUser, 'SuperAdmin')
        || userHasRole(state.currentUser, 'Operator')),
    currentRealUserIsSuperAdmin: state => userHasRole(state.currentUser, 'SuperAdmin'),
    currentUser: state =>
      pipe(
        state.impersonatingUserId,
        O.fold(
          () => state.currentUser as number,
          (impersonatingUserId: number) =>
            pipe(
              state.clients as { id: number }[], // TODO: review this
              A.find((
                // TODO: review this
                clientUser: { id: number }
              ) => clientUser.id === impersonatingUserId),
              O.getOrElse(() => defaultMockUser)
            )
        )
      ),
    User: state => state.currentUser,
    Clients: state => state.clients,
    Contacts: state => state.contacts,
    TripUsers: (state, getters) => {
      if (!getters.currentTripDashboardId) {
        return []
      }

      // Client Users are users in the 'clients' relationship
      // Preview Users (userViewers) should NEVER appear in TripUsers
      // Get the list of userViewers to filter them out completely
      const userViewers = getters.TripUserViewers || []
      const viewerIds = new Set(userViewers.map((viewer: any) => viewer.id))

      // Get all users from the backend (should only be client users)
      const allUsersFromBackend = getters.Trips[getters.currentTripDashboardId].users || []

      // Filter out ANY user that is in userViewers - they should only appear in TripUserViewers
      // Also filter out users marked as non-customer
      const filteredUsers = allUsersFromBackend.filter((tripUser: any) => {
        // Exclude users marked as non-customer
        if (tripUser.is_non_customer === true) {
          return false
        }

        // Exclude ANY user that is in userViewers - they belong in TripUserViewers, not TripUsers
        if (viewerIds.has(tripUser.id)) {
          return false
        }

        // User is not in userViewers and not marked as non-customer, so they're a regular client user
        return true
      })

      // Enrich with client data
      return _map(filteredUsers, tripUser => {
        const client = _find(state.clients, clientItem => tripUser.id === clientItem.id)
        if (client) {
          _assign(tripUser, client)
        }
        return tripUser
      })
    },
    TripUserViewers: (state, getters) => {
      // Get non-customer users (viewers) separately from regular users
      if (!getters.currentTripDashboardId) {
        return []
      }
      const trip = getters.Trips[getters.currentTripDashboardId]
      if (!trip) {
        return []
      }
      // userViewers can be at the root level or inside data.userViewers
      return trip.userViewers || trip.data?.userViewers || []
    },
    // Check if current user is a non-customer user on the current dashboard
    isCurrentUserNonCustomer: (state, getters) => {
      if (!getters.currentTripDashboardId || !state.currentUser) {
        return false
      }
      // Check in userViewers instead of TripUsers
      const viewer = _find(getters.TripUserViewers, {
        id: state.currentUser.id
      })
      return !!viewer
    },
    // Check if current user is a non-customer user on ANY dashboard (works even without currentTripDashboardId)
    isCurrentUserNonCustomerOnAnyDashboard: (state, getters) => {
      if (!state.currentUser) {
        return false
      }

      // First, check the flag from appInitialData (works even when no trips are loaded)
      const flagFromInitialData = _get(
        window,
        'appInitialData.is_non_customer_on_any_dashboard',
        false
      )
      if (flagFromInitialData) {
        return true
      }

      // Fallback: Check all trips in the collection (if trips are loaded)
      const trips = getters.tripDashboards || {}
      const tripIds = Object.keys(trips)

      for (const tripId of tripIds) {
        const trip = trips[tripId]
        // Check userViewers instead of users with is_non_customer flag
        const userViewers = trip?.userViewers || trip?.data?.userViewers || []

        if (Array.isArray(userViewers) && userViewers.length > 0) {
          const viewer = _find(userViewers, {
            id: state.currentUser.id
          })
          if (viewer) {
            return true
          }
        }
      }
      return false
    },
    isCurrentUserReadOnly: (state, getters) => {
      if (
        !getters.isCurrentUserNonCustomer || !getters.currentTripDashboardId || !state.currentUser
      ) {
        return false
      }
      // Check in userViewers instead of TripUsers
      const viewer = _find(getters.TripUserViewers, {
        id: state.currentUser.id
      })
      return viewer && (viewer as any).is_read_only === true
    },
    isOperator: (state, getters) => {
      // Read-only users should never be treated as operators, even in OPERATOR_VIEW mode
      if (getters.isCurrentUserReadOnly) {
        return false
      }
      // If user is non-customer, check if they're in OPERATOR_VIEW mode
      if (getters.isCurrentUserNonCustomer) {
        return (getters.currentRealUserIsOperator
          && pipe(
              state.currentViewMode,
              O.getOrElse(() => TripDashboardViewModes.CLIENT_VIEW)
            ) === TripDashboardViewModes.OPERATOR_VIEW)
      }
      return getters.currentRealUserIsOperator
    },
    canAccessSection: (state, getters) =>
      (sectionName: string) => {
        if (!getters.Dashboard[`dashboard-section-enabled-${sectionName}`]) {
          return false
        } else if (getters.isOperator) {
          return true
        } // @todo this is imperfect (why?!)
        else if (getters.currentTripDashboardId && getters.User) {
          // Check if user is a non-customer user (viewer)
          // Non-customer users have permissions: null, which means "all permissions" (but read-only)
          // So they should see all sections enabled in the dashboard
          if (getters.isCurrentUserNonCustomer) {
            return true // Non-customer users can see all enabled sections
          }

          // Regular client users
          const client = _find(getters.Trips[getters.currentTripDashboardId].users, clientItem =>
            // console.log(getters.User, client);
            getters.User.id === clientItem.id)
          if (client) {
            return !(client.permissions && client.permissions[sectionName] === false)
          }
        } else if (getters.User) {
          return !(getters.User.permissions && getters.User.permissions[sectionName] === false)
        } else {
          return false
        }
      },
    getTripUser: (_state, getters) =>
      (userId: number) => _find(getters.TripUsers, client => client.id === userId),
    currentUserHasPermissionTo: (_state, getters) =>
      (permission: string) =>
        (getters.User.permissions ?? [])
          .filter((r: { name: string; value: string }) =>
            r.name === permission || r.value === permission
          )
          .length
  },
  mutations: {
    syncClientExtras(state, user) {
      if (!Array.isArray(state.clients)) {
        state.clients = []
        return
      }

      const clientIndex = state.clients.findIndex(client => client.id === user.id)

      if (clientIndex !== -1) {
        state.clients[clientIndex].extras = user.extras
      }
    },
    setClientUsers(state, payload) {
      state.clients = payload

      // Note: Non-customer users (viewers) are now sent separately as userViewers,
      // so we don't need to check is_non_customer flag here anymore
    },
    impersonateUser(state, userIdOpt) {
      state.impersonatingUserId = userIdOpt
      state.currentViewMode = O.some(
        O.isSome(userIdOpt) ?
          TripDashboardViewModes.CLIENT_VIEW :
          TripDashboardViewModes.OPERATOR_VIEW
      )
    },
    setViewMode(state, viewModeOpt) {
      state.currentViewMode = viewModeOpt
    },
    setContactsState(state, payload) {
      state.contacts = payload
    }
  },
  actions: {
    updateUserExtras(context, payload) {
      axios
        .post(`/clients/${payload.user.id}/updateExtras`, {
          extras: payload.user.extras
        })
        .then(response => {
          if (response.status === 200) {
            context.commit('syncClientExtras', payload.user)
          }
        })
    },
    createUser(context, payload) {
      // Use currentRealUserIsOperator to allow operators who are also viewers
      if (this.getters.currentRealUserIsOperator) {
        return axios
          .post(`${context.getters.currentTripDashboardId}/createUser`, payload)
          .then(response => {
            if (response.status === 200) {
              context.commit('updateTripUsers', response.data.users)

              const trip = context.getters.tripDashboards[context.getters.currentTripDashboardId]
              trip.sign_requests = response.data.sign_requests
              setupDashboard(context, context.getters.currentTripDashboardId, trip)

              response.data.users.forEach((user: any) => {
                context.commit('addClientIfNotExists', user)
              })

              return response
            }
          })
          .catch(error => {
            alertAxiosError(error, 'Could not create the client user. Please try again.')
            return Promise.reject(error)
          })
      }
      return Promise.reject(new Error('User is not an operator'))
    },

    addUser(context, payload) {
      // Use currentRealUserIsOperator instead of isOperator to allow operators who are also viewers
      if (this.getters.currentRealUserIsOperator) {
        return axios
          .post(`${context.getters.currentTripDashboardId}/addUser`, payload)
          .then(response => {
            if (response.status === 200) {
              context.commit('updateTripUsers', response.data.users)

              const trip = context.getters.tripDashboards[context.getters.currentTripDashboardId]
              trip.sign_requests = response.data.sign_requests
              setupDashboard(context, context.getters.currentTripDashboardId, trip)

              response.data.users.forEach((user: any) => {
                context.commit('addClientIfNotExists', user)
              })

              return response
            }
          })
          .catch(error => {
            alertAxiosError(error, 'Could not add the client user to this trip. Please try again.')
            return Promise.reject(error)
          })
      }
      return Promise.reject(new Error('User is not an operator'))
    },

    unlinkUser(context, payload) {
      // Use currentRealUserIsOperator instead of isOperator to allow operators who are also viewers
      if (this.getters.currentRealUserIsOperator) {
        return axios
          .post(`${context.getters.currentTripDashboardId}/unlinkUser`, payload)
          .then(response => {
            if (response.status === 200) {
              context.commit('updateTripUsers', response.data.users)

              const trip = context.getters.tripDashboards[context.getters.currentTripDashboardId]
              trip.sign_requests = response.data.sign_requests
              setupDashboard(context, context.getters.currentTripDashboardId, trip)

              context.commit(
                'removeClientsNotInList',
                response.data.users.map((user: any) => user.id)
              )

              return response
            }
          })
          .catch(error => {
            alertAxiosError(
              error,
              'Could not remove the client user from this trip. Please try again.'
            )
            return Promise.reject(error)
          })
      }
      return Promise.reject(new Error('User is not an operator'))
    },

    setUserPermission(context, payload) {
      // Use currentRealUserIsOperator instead of isOperator to allow operators who are also viewers
      if (this.getters.currentRealUserIsOperator) {
        return axios
          .post(`${context.getters.currentTripDashboardId}/setUserPermission`, payload)
          .then(response => {
            if (response.status === 200) {
              context.commit('updateTripUsers', response.data.users)
              TripDashboardStore.commit('setClientUsers', response.data.users)

              return response
            }
          })
          .catch(error => {
            alertAxiosError(error, 'Could not update permissions for this user. Please try again.')
            return Promise.reject(error)
          })
      }
      return Promise.reject(new Error('User is not an operator'))
    },
    getContacts(context, payload) {
      if (this.getters.isOperator && payload && payload.length > 2) {
        return axios
          .post(`${context.getters.currentTripDashboardId}/getContacts`, { search_key: payload })
          .then(response => {
            if (response.status === 200) {
              context.commit('setContactsState', response.data)
            }
          })
      } else {
        context.commit('setContactsState', [])
      }
    }
  }
}
